import { useEffect, useState, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Input, Divider, Spinner, Heading,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  getOrganization,
  updateOrganization,
  type OrganizationDto,
  type UpdateOrganizationRequest,
} from '../api/organizationApi';
import { LogoUpload } from '../components/LogoUpload';

export function OrganizationSettingsPage() {
  const { t } = useTranslation();
  const [org, setOrg] = useState<OrganizationDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [form, setForm] = useState<UpdateOrganizationRequest>({});

  const loadOrg = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getOrganization();
      setOrg(data);
      setForm({
        name: data.name,
        legalName: data.legalName ?? '',
        address: data.address ?? {},
        taxIdentifier: data.taxIdentifier ?? '',
        taxIdentifierType: data.taxIdentifierType ?? '',
        contactEmail: data.contactEmail ?? '',
        contactPhone: data.contactPhone ?? '',
        websiteUrl: data.websiteUrl ?? '',
        rowVersion: data.rowVersion,
      });
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrg(); }, [loadOrg]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const updated = await updateOrganization(form);
      setOrg(updated);
      setForm((prev) => ({ ...prev, rowVersion: updated.rowVersion }));
      setSuccess(t('organization.savedSuccessfully'));
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Flex flex={1} justify="center" align="center" p={4}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Box flex={1} p={4} maxW="800px">
      <Heading as="h2" size="lg" mb={4}>{t('organization.settings')}</Heading>

      {error && (
        <Flex bg="red.50" p={3} borderRadius="md" mb={3}>
          <Text color="red.500">{error}</Text>
        </Flex>
      )}
      {success && (
        <Flex bg="green.50" p={3} borderRadius="md" mb={3}>
          <Text color="green.500">{success}</Text>
        </Flex>
      )}

      <Box>
        <Text fontWeight="600" mb={1}>{t('organization.name')}</Text>
        <Input
          value={form.name ?? ''}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          placeholder={t('organization.namePlaceholder')}
          mb={3}
        />

        <Text fontWeight="600" mb={1}>{t('organization.legalName')}</Text>
        <Input
          value={form.legalName ?? ''}
          onChange={(e) => setForm((prev) => ({ ...prev, legalName: e.target.value }))}
          placeholder={t('organization.legalNamePlaceholder')}
          mb={3}
        />

        <Divider my={3} />

        <Text fontWeight="600" fontSize="lg" mb={2}>{t('organization.address')}</Text>

        <Text fontWeight="600" mb={1}>{t('organization.streetAddress')}</Text>
        <Input
          value={form.address?.streetAddress ?? ''}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, address: { ...prev.address, streetAddress: e.target.value } }))
          }
          mb={3}
        />

        <Flex gap={3} mb={3}>
          <Box flex={1}>
            <Text fontWeight="600" mb={1}>{t('organization.city')}</Text>
            <Input
              value={form.address?.city ?? ''}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, address: { ...prev.address, city: e.target.value } }))
              }
            />
          </Box>
          <Box flex={1}>
            <Text fontWeight="600" mb={1}>{t('organization.stateProvince')}</Text>
            <Input
              value={form.address?.stateProvince ?? ''}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, address: { ...prev.address, stateProvince: e.target.value } }))
              }
            />
          </Box>
        </Flex>

        <Flex gap={3} mb={3}>
          <Box flex={1}>
            <Text fontWeight="600" mb={1}>{t('organization.postalCode')}</Text>
            <Input
              value={form.address?.postalCode ?? ''}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, address: { ...prev.address, postalCode: e.target.value } }))
              }
            />
          </Box>
          <Box flex={1}>
            <Text fontWeight="600" mb={1}>{t('organization.country')}</Text>
            <Input
              value={form.address?.country ?? ''}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, address: { ...prev.address, country: e.target.value } }))
              }
            />
          </Box>
        </Flex>

        <Divider my={3} />

        <Text fontWeight="600" fontSize="lg" mb={2}>{t('organization.taxInfo')}</Text>

        <Flex gap={3} mb={3}>
          <Box flex={1}>
            <Text fontWeight="600" mb={1}>{t('organization.taxIdentifier')}</Text>
            <Input
              value={form.taxIdentifier ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, taxIdentifier: e.target.value }))}
            />
          </Box>
          <Box flex={1}>
            <Text fontWeight="600" mb={1}>{t('organization.taxIdentifierType')}</Text>
            <Input
              value={form.taxIdentifierType ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, taxIdentifierType: e.target.value }))}
            />
          </Box>
        </Flex>

        <Divider my={3} />

        <Text fontWeight="600" fontSize="lg" mb={2}>{t('organization.contact')}</Text>

        <Text fontWeight="600" mb={1}>{t('organization.contactEmail')}</Text>
        <Input
          value={form.contactEmail ?? ''}
          onChange={(e) => setForm((prev) => ({ ...prev, contactEmail: e.target.value }))}
          placeholder="email@example.com"
          mb={3}
        />

        <Text fontWeight="600" mb={1}>{t('organization.contactPhone')}</Text>
        <Input
          value={form.contactPhone ?? ''}
          onChange={(e) => setForm((prev) => ({ ...prev, contactPhone: e.target.value }))}
          placeholder="+380..."
          mb={3}
        />

        <Text fontWeight="600" mb={1}>{t('organization.websiteUrl')}</Text>
        <Input
          value={form.websiteUrl ?? ''}
          onChange={(e) => setForm((prev) => ({ ...prev, websiteUrl: e.target.value }))}
          placeholder="https://..."
          mb={3}
        />

        <Divider my={3} />

        <Text fontWeight="600" fontSize="lg" mb={2}>{t('organization.logo')}</Text>
        <LogoUpload
          logoUrl={org?.logoUrl}
          onLogoChange={(url) => setOrg((prev) => (prev ? { ...prev, logoUrl: url } : prev))}
          onError={(msg) => setError(msg)}
          onSuccess={(msg) => setSuccess(msg)}
        />

        <Divider my={3} />

        <Flex gap={3} justify="flex-end" pt={2}>
          <Button onClick={loadOrg} isDisabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button colorScheme="brand" onClick={handleSave} isDisabled={saving}>
            {saving ? <Spinner size="sm" /> : t('common.save')}
          </Button>
        </Flex>
      </Box>
    </Box>
  );
}
